import { useEffect } from "react";
import Link from "next/link";

import { apiUrls } from "config";

import Tag from "ui-components/Tag";
import Button from "ui-components/Button";
import Skeleton from "ui-components/Skeleton";

import { DeleteEntity, ShareEntity } from "lib/permissions";
import CreateDataFileMacro from "modules/macros/elements/Create-Data-File-Macro";

import { useGetUserDetails } from "auth/auth-state";

import { useFetchDataFileListing } from "modules/data-files/api";
import RenameFile from "modules/data-files/elements/Rename-File";

const Listing = () => {
  const userDetails = useGetUserDetails();
  const [data, status, hasNextPage, getNextPage] = useFetchDataFileListing();
  useEffect(() => {
    hasNextPage && getNextPage();
  }, [getNextPage, hasNextPage, data]);

  const listing = data.flat();

  const empty = status !== "loading" && listing.length === 0;
  return (
    <div className="max-w-screen-xl mx-auto px-4 sm:px-6">
      <div className="pb-4 mb-8">
        <h1 className="h2 text-center">Data Files for Analysis</h1>
      </div>
      {empty && (
        <div className="text-center">
          <p>
            <Link href="/data-upload">
              <a>
                <Button>Upload your First Data File</Button>
              </a>
            </Link>
          </p>
        </div>
      )}
      {!empty && listing.length === 0 && <Skeleton />}
      {listing.length >= 1 && (
        <table className="table table-auto w-full">
          <thead>
            <tr>
              <th className="font-semibold text-left table-cell w-1/6">
                <p className="px-1 mx-1">Creation Details</p>
              </th>
              <th className="font-semibold text-left table-cell w-1/2">
                <p className="px-1 mx-1">Data File</p>
              </th>
              <th className="font-semibold text-left table-cell">
                <p className="px-1 mx-1">Size</p>
              </th>
            </tr>
          </thead>
          <tbody>
            {listing.map((d) => (
              <tr key={d.id} className="table-row border-t-2">
                <td className="table-cell">
                  <div className="m-1">
                    <p>
                      {new Date(d.created_time).toLocaleDateString(undefined, {
                        hour: "numeric",
                        minute: "numeric",
                        hour12: true,
                      })}
                    </p>
                    <p>{d.create_type}</p>
                    {d.create_type !== "INGESTION" && (
                      <p>
                        Source Files:{" "}
                        {d.source_data.map((s) => (
                          <Link key={s} href={`/data-files/${s}`}>
                            <a className="underline text-primary-main pr-2">
                              {data.flat().find((f) => f.id === s)?.name || s}
                            </a>
                          </Link>
                        ))}
                      </p>
                    )}
                  </div>
                </td>
                <td className="table-cell">
                  <div className="m-1">
                    <p className="text-primary-main cursor-pointer font-semibold">
                      <Link href={`/data-files/${d.id}`}>{d.name}</Link>
                    </p>
                    {userDetails && (
                      <div className="flex flex-wrap items-center mt-1">
                        {d.owner !== userDetails.email ? (
                          <Tag className="text-base">Shared by {d.owner}</Tag>
                        ) : (
                          <div className="flex flex-wrap">
                            <CreateDataFileMacro
                              dataFile={d}
                              buttonColor="gray"
                              buttonSize="small"
                            />
                            <ShareEntity
                              entityName="Data File"
                              baseUrl={apiUrls.data}
                              entityId={d.id}
                              size="small"
                            />
                            <DeleteEntity
                              entityName="Data File"
                              baseUrl={apiUrls.data}
                              entityId={d.id}
                              size="small"
                            />
                            <RenameFile
                              dataFileId={d.id}
                              currentName={d.name}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </td>
                <td className="table-cell">
                  <p className="m-1">
                    {d.col_count.toLocaleString()} columns and{" "}
                    {d.row_count.toLocaleString()} rows.
                  </p>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default Listing;
